import { useState, useRef, useEffect } from "react";
import cx from "classnames";
import { Input } from "@repo/ping-design";
import type { RenderEditCellProps } from "react-data-grid";
import { useCellSave } from "../useCellSave";
import { DOLLAR_INPUT_OPTIONS, parseLiteralDollarInput, normalizeDollarInput } from "../dollarInput";
import type { TopBuildingsListRow } from "../TopBuildingsList";

type Props = RenderEditCellProps<TopBuildingsListRow> & {
  onSave: (value: number | null) => Promise<boolean>;
};

export function MoneyCellEditor({ row, column, onSave, onClose }: Props) {
  const displayValue = String(row.values[column.key] ?? "");
  const originalParsed = parseLiteralDollarInput(displayValue);
  const [value, setValue] = useState(displayValue);
  const { status, runSave } = useCellSave();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef(displayValue);
  const savedRef = useRef(false);

  const getParsed = (): number | null => parseLiteralDollarInput(normalizeDollarInput(valueRef.current));

  const handleSave = async (): Promise<boolean> => {
    if (savedRef.current) return true;
    const parsed = getParsed();
    if (parsed === null || parsed === originalParsed) return true;
    savedRef.current = true;
    const ok = await runSave(() => onSave(parsed));
    if (!ok) savedRef.current = false;
    return ok;
  };

  const unmountSaveRef = useRef(() => {});
  unmountSaveRef.current = () => {
    if (savedRef.current) return;
    const parsed = getParsed();
    if (parsed === null || parsed === originalParsed) return;
    savedRef.current = true;
    onSave(parsed);
  };

  useEffect(() => () => unmountSaveRef.current(), []);

  return (
    <div
      ref={wrapperRef}
      className={cx("TopBuildingsList__CellEditorWrapper", {
        "TopBuildingsList__CellEditorWrapper--Error": status === "error",
      })}
      onBlur={async (e) => {
        if (wrapperRef.current?.contains(e.relatedTarget as Node)) return;
        const ok = await handleSave();
        if (ok) onClose(true);
      }}
    >
      <Input
        name={column.key}
        value={value}
        autoFocus
        style={{ minHeight: "20px" }}
        disabled={status === "saving"}
        prefix={<span>$</span>}
        options={DOLLAR_INPUT_OPTIONS}
        useDebounce={false}
        onChange={(_name, v) => {
          setValue(String(v));
          valueRef.current = String(v);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            savedRef.current = true;
            onClose(false);
          }
          if (e.key === "Enter") {
            e.stopPropagation();
            handleSave().then((ok) => {
              if (ok) onClose(true);
            });
          }
        }}
      />
    </div>
  );
}
